import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { LeafletEvent, ZoomAnimEvent } from 'leaflet';

import 'leaflet/dist/leaflet.css';

import { mapClicked, mapZoomEnd } from 'actions/mapAdmin';
import { IArea, IStateAwareArea } from "types/map";
import { IAppState } from "types/app";

import { ModalTooltip } from 'components/ModalTooltip/ModalTooltipContainer';

import { isDistrict, isLodgings, isSubLodgings, shouldAppearOnMap } from './util';
import { MIN_ZOOM_LEVEL_FOR_DESTINATIONS, } from "./constants";
import Lodgings from "./Lodgings";
import MapModalTooltipContext from 'components/Map/MapModalTooltipContext';
import SelectedAreaContext from './SelectedAreaContext';

import PixiMap from './PixiMap';
import Limbo from './Limbo';
import asStateAwareArea from 'mapping/asStateAwareArea';
import FallbackMap from 'components/Map/FallbackMap';
import preloadMapSprites from 'components/Map/preloadMapSprites';
import { spriteLoaderProgress } from 'actions/spriteLoader';
import isWebGLSupported from 'startup/isWebGLSupported';

export type Props = {
  areas?: IArea[],
  currentArea: IArea,
  dispatch: Function,
  fallbackMapPreferred: boolean,
  initialCenter: number[],
  initialZoom: number,
  isChangingArea: boolean,
  onAreaClick: (area: IArea) => Promise<void>,
  onWillUnmount: (zoom: number, center: number[]) => void,
}

export interface State {
  center: number[],
  isModalTooltipOpen: boolean,
  selectedArea?: IStateAwareArea,
  tooltipData?: any,
  zoomLevel: number,
}

export class MapComponent extends Component<Props, State> {
  static displayName = 'MapComponent';

  state: State;

  constructor(props: Props) {
    super(props);
    this.state = {
      center: props.initialCenter,
      isModalTooltipOpen: false,
      zoomLevel: props.initialZoom,
    };
  }

  componentDidMount(): void {
    console.info('MapComponent.compnentDidMount()');
    const { areas, dispatch, fallbackMapPreferred } = this.props;
    const { zoomLevel } = this.state;
    dispatch(mapZoomEnd(zoomLevel));

    // Load map sprites
    if (!fallbackMapPreferred) {
      // Load sprites
      preloadMapSprites(areas ?? [],
        ({ progress }) => {
          dispatch(spriteLoaderProgress(progress));
        },
      );
    }
  }

  /**
   * Before unmounting, tell our parent what the map's centre & zoom were when we shut down
   */
  componentWillUnmount(): void {
    const { onWillUnmount } = this.props;
    const { center, zoomLevel } = this.state;
    onWillUnmount(zoomLevel, center);
  }

  // noinspection JSUnusedLocalSymbols
  handleAreaClick = async (e: any, area: IArea) => {
    const { onAreaClick } = this.props;
    console.info(`MapComponent.onAreaClick(area=${area.name})`);
    // Run the parent callback
    await onAreaClick(area);
  };

  handleAreaSelect = (area?: IArea) => {
    const { areas, currentArea } = this.props;
    // console.info(`MapComponent.handleAreaSelect(${area?.name || area?.areaKey})`);
    if (!area) {
      this.setState({ selectedArea: area });
      return;
    }
    const stateAwareArea = asStateAwareArea(area, areas || [], currentArea);
    if (!stateAwareArea.isLit) {
      this.setState({ selectedArea: undefined });
      return;
    }
    this.setState({ selectedArea: stateAwareArea });
    return;
  };

  handleClick = ({ latlng }: { latlng: any }) => {
    const { dispatch } = this.props;
    dispatch(mapClicked(latlng.lng, latlng.lat));
  };

  handleHitboxClick = () => {
  };

  handleMoveEnd = (e: LeafletEvent) => {
    const mapCenter = e.target.getCenter();
    const zoomLevel = e.target.getZoom();
    this.setState({
      zoomLevel,
      center: [
        mapCenter.lng,
        mapCenter.lat,
      ],
    });
  };

  handleOpenModalTooltip = (tooltipData: any) => {
    this.setState({
      tooltipData,
      isModalTooltipOpen: true,
    });
  };

  handleRequestCloseModalTooltip = (_: any) => {
    // Close the modal
    this.setState({
      isModalTooltipOpen: false,
      tooltipData: undefined,
    });

    // Clear the selected area
    this.handleAreaSelect();
  };

  handleZoomEnd = (e: ZoomAnimEvent) => {
    const { dispatch, fallbackMapPreferred } = this.props;
    const { selectedArea } = this.state;
    const zoomLevel = e.target.getZoom();
    this.setState({ zoomLevel });
    dispatch(mapZoomEnd(zoomLevel));

    // If we have zoomed in while moused over an area's hitbox, then we need to automatically
    // deselect it
    if (
      zoomLevel >= MIN_ZOOM_LEVEL_FOR_DESTINATIONS
      && isDistrict(selectedArea)
      && !fallbackMapPreferred
    ) {
      this.handleAreaSelect();
    }
  };

  render() {
    const {
      areas,
      currentArea,
      fallbackMapPreferred,
      initialCenter,
      initialZoom,
    } = this.props;

    const {
      isModalTooltipOpen,
      selectedArea,
      tooltipData,
      zoomLevel,
    } = this.state;

    const isPlayerInLimbo = !isLodgings(currentArea) && !isSubLodgings(currentArea, areas || []) && !shouldAppearOnMap(currentArea);

    // One final check as we render to try and funnel the user to the compatibility map rather than crashing
    const shouldWeShowTheFallbackMap = fallbackMapPreferred || !isWebGLSupported();

    return (
      <MapModalTooltipContext.Provider value={{
        openModalTooltip: this.handleOpenModalTooltip,
        onRequestClose: () => {
        }
      }}>
        <SelectedAreaContext.Provider value={{ selectedArea }}>
          {shouldWeShowTheFallbackMap ? (
            <FallbackMap
              currentArea={currentArea}
              initialCenter={initialCenter}
              initialZoom={initialZoom}
              onAreaClick={this.handleAreaClick}
              onAreaSelect={this.handleAreaSelect}
              onClick={this.handleClick}
              onHitboxClick={this.handleHitboxClick}
              onMoveEnd={this.handleMoveEnd}
              onZoomEnd={this.handleZoomEnd}
              selectedArea={selectedArea}
              zoomLevel={zoomLevel}
            />
          ) : (
            <Fragment>
              <PixiMap
                currentArea={currentArea}
                initialCenter={initialCenter}
                initialZoom={initialZoom}
                onAreaClick={this.handleAreaClick}
                onAreaSelect={this.handleAreaSelect}
                onClick={this.handleClick}
                onHitboxClick={this.handleHitboxClick}
                onMoveEnd={this.handleMoveEnd}
                onZoomEnd={this.handleZoomEnd}
                selectedArea={selectedArea}
                zoomLevel={zoomLevel}
              />

              {isPlayerInLimbo && (<Limbo />)}

              <Lodgings
                onAreaClick={this.handleAreaClick}
                onAreaSelect={this.handleAreaSelect}
                selectedArea={selectedArea}
              />

              <ModalTooltip
                modalIsOpen={isModalTooltipOpen}
                onRequestClose={this.handleRequestCloseModalTooltip}
                tooltipData={tooltipData}
                disableTouchEvents
              />
            </Fragment>
          )}
        </SelectedAreaContext.Provider>
      </MapModalTooltipContext.Provider>
    );
  }
}

const mapStateToProps = ({ map: { areas, currentArea, fallbackMapPreferred } }: IAppState) => ({
  areas,
  fallbackMapPreferred,
  currentArea: currentArea!,
});

export default connect(mapStateToProps)(MapComponent);
